import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Users, LogOut, BookOpen, Database,
  ChevronRight, CalendarDays, Plus, Flag, ClipboardList,
  Eye, Pencil, BarChart3, Mail, Settings as SettingsIcon, Shield,
  MoreVertical, RotateCcw
} from "lucide-react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import MessagePopup from "@/components/MessagePopup";
import AppFooter from "@/components/AppFooter";
import PlayerGuide from "@/components/PlayerGuide";
import AdminGuide from "@/components/AdminGuide";
import AdminNavBar from "@/components/AdminNavBar";
import Logo from "@/components/Logo";
import InstallAppBanner from "@/components/InstallAppBanner";
import CompetitionFormDialog from "@/components/admin/CompetitionFormDialog";
import DestructiveConfirmDialog from "@/components/DestructiveConfirmDialog";
import AuditLogModal from "@/components/admin/AuditLogModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatUK = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
};

// Returns { label, tone } when comp ends soon (≤ 7 days), or null otherwise.
const computeUrgency = (endDateIso) => {
  if (!endDateIso) return null;
  const end = new Date(endDateIso);
  if (Number.isNaN(end.getTime())) return null;
  const today = new Date();
  // Compare on calendar-day boundaries (UK local time) to avoid off-by-one near midnight.
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const startOfEnd = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  const days = Math.round((startOfEnd - startOfToday) / 86400000);
  if (days < 0) return null;            // already ended
  if (days === 0) return { label: "Ends today", tone: "danger" };
  if (days === 1) return { label: "Ends tomorrow", tone: "danger" };
  if (days <= 3) return { label: `Ends in ${days} days`, tone: "danger" };
  if (days <= 7) return { label: `Ends in ${days} days`, tone: "warn" };
  return null;
};

export default function Dashboard() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [unreadMessages, setUnreadMessages] = useState([]);
  const [showMessagePopup, setShowMessagePopup] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [competitions, setCompetitions] = useState([]); // joined
  const [availableComps, setAvailableComps] = useState([]); // open to join, not yet joined
  const [joiningId, setJoiningId] = useState(null);
  const [showAvailable, setShowAvailable] = useState(false); // collapsed by default if user has 3+ joined
  const [adminCompetitions, setAdminCompetitions] = useState([]);
  const [adminStats, setAdminStats] = useState({ players: null, courses: null });
  // After joining a comp we flash-highlight its row & scroll it into view so it doesn't get
  // lost at the bottom of the list (the joined list is sorted by end-date ascending).
  const [justJoinedId, setJustJoinedId] = useState(null);

  // Withdraw flow — opened from any "Your competitions" row. Surfaces the
  // existing /competitions/{id}/withdraw endpoint with the destructive
  // confirmation copy users expect (all rounds + scores will be deleted).
  const [withdrawingComp, setWithdrawingComp] = useState(null);
  const [withdrawSubmitting, setWithdrawSubmitting] = useState(false);

  // Past competitions — comps the player participated in whose end_date has
  // passed. Shown in a separate "Past competitions" panel so the live
  // dashboard stays focused on current play but the player can still drill
  // into their historical Best Eclectic for any past comp.
  const [pastComps, setPastComps] = useState([]);

  // Inline create / edit competition dialogs (consolidated from former AdminCompetitions page)
  const [showCreateComp, setShowCreateComp] = useState(false);
  const [showEditComp, setShowEditComp] = useState(false);
  const [editingComp, setEditingComp] = useState(null);
  const blankCompForm = {
    name: "",
    start_date: "",
    end_date: "",
    total_rounds: 10,
    allow_selective_entry: true,
    recognize_score_of_2: false,
    hole_format: "18",
    course_id: "",
    tee_id: ""
  };
  const [compForm, setCompForm] = useState(blankCompForm);
  const [compFormSaving, setCompFormSaving] = useState(false);
  const [courseOptions, setCourseOptions] = useState([]);
  const [courseDetails, setCourseDetails] = useState({}); // { course_id: course_with_tees }

  // Delete-from-edit flow — destructive AlertDialog opened from the
  // "Delete competition" button at the bottom of the Edit modal. Lives
  // here (not inside the comp's View page) because that's where admins
  // already are when they decide to mutate or remove a comp; iter 28
  // bug report confirmed the buried View-page Delete was undiscoverable.
  const [deletingComp, setDeletingComp] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [auditComp, setAuditComp] = useState(null);  // {id, name, end_date} or null

  useEffect(() => {
    if (!user) { navigate("/"); return; }
    if (user.role === "player") {
      fetchUnreadGeneralMessages();
      fetchPlayerCompetitions();
    } else if (user.role === "admin") {
      fetchAdminData();
    }
  }, [user, navigate]);

  const fetchPlayerCompetitions = async () => {
    try {
      // Pull joined comps + every active comp in parallel; the diff is "available to join".
      const [mine, all] = await Promise.all([
        axios.get(`${API}/player/my-competitions`, { withCredentials: true }),
        axios.get(`${API}/competitions`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      const joined = mine.data || [];
      const joinedIds = new Set(joined.map((c) => c.id));
      // Defensive belt-and-braces filter: backend now excludes ended comps
      // and tags `status`, but if a stale cached response or a near-midnight
      // race lands an ended comp in the payload, drop it client-side too.
      // `end_date` is stored as ISO YYYY-MM-DD so a plain string compare is
      // a valid date compare.
      const todayIso = new Date().toISOString().slice(0, 10);
      const available = (all.data || []).filter((c) =>
        !joinedIds.has(c.id)
        && c.status !== "ended"
        && (!c.end_date || c.end_date >= todayIso)
      );
      setCompetitions(joined);
      setAvailableComps(available);
      // Split active vs past from `joined`: backend returns ALL the player's
      // registrations (including ones whose comp has ended). Active list
      // shows only end_date >= today so the dashboard stays focused on live
      // play; past list powers the "Past competitions" history panel.
      const pastList = joined.filter((c) => c.end_date && c.end_date < todayIso);
      setPastComps(pastList);
      // Keep "Available to join" COLLAPSED by default. Users open it via the
      // prominent green banner CTA or the "X available to join" link in the
      // Your-competitions header — having both expanded was cluttering the
      // dashboard hero (esp. on mobile).
      setShowAvailable(false);
    } catch (e) { console.error("Comps load failed", e); }
  };

  // Withdraw confirm handler — fires after the user clicks "Leave & Delete
  // Data" in the dialog. Reuses the existing /competitions/{id}/withdraw
  // endpoint that's been there all along.
  const handleWithdraw = async () => {
    if (!withdrawingComp) return;
    setWithdrawSubmitting(true);
    try {
      await axios.post(`${API}/competitions/${withdrawingComp.id}/withdraw`, {}, { withCredentials: true });
      toast.success(`Withdrawn from ${withdrawingComp.name}`, {
        description: "Your scores are preserved for 7 days — you can rejoin from your dashboard.",
      });
      setWithdrawingComp(null);
      fetchPlayerCompetitions();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to withdraw");
    } finally {
      setWithdrawSubmitting(false);
    }
  };

  // Self-serve rejoin after an accidental withdraw (Feb 2026 production fix).
  // Backend enforces a 7-day window; UI just shows the button on withdrawn
  // comps returned by /player/my-competitions.
  const handleRejoin = async (comp) => {
    try {
      await axios.post(`${API}/competitions/${comp.id}/rejoin`, {}, { withCredentials: true });
      toast.success(`Rejoined ${comp.name}`, { description: "All your scores were preserved." });
      fetchPlayerCompetitions();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to rejoin");
    }
  };

  const handleJoin = async (compId) => {
    setJoiningId(compId);
    try {
      await axios.post(`${API}/competitions/${compId}/join`, {}, { withCredentials: true });
      toast.success("Joined — find it in 'Your competitions'");
      await fetchPlayerCompetitions();
      // Flash-highlight + scroll the newly joined row into view so it's not lost at the bottom.
      setJustJoinedId(compId);
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="comp-row-${compId}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
      setTimeout(() => setJustJoinedId(null), 4000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not join competition");
    } finally {
      setJoiningId(null);
    }
  };
  const fetchAdminData = async () => {
    try {
      const [comps, players, courses] = await Promise.all([
        axios.get(`${API}/admin/competitions`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/players`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/courses`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setAdminCompetitions(comps.data || []);
      setAdminStats({ players: (players.data || []).length, courses: (courses.data || []).length });
      setCourseOptions(courses.data || []);
    } catch (e) { console.error("Admin data load failed", e); }
  };

  const fetchCourseDetails = async (courseId) => {
    if (!courseId || courseDetails[courseId]) return;
    try {
      const r = await axios.get(`${API}/courses/${courseId}`, { withCredentials: true });
      setCourseDetails((prev) => ({ ...prev, [courseId]: r.data }));
    } catch { /* non-fatal */ }
  };

  const openCreateComp = () => {
    setCompForm(blankCompForm);
    setShowCreateComp(true);
  };

  const openEditComp = (comp) => {
    setEditingComp(comp);
    setCompForm({
      name: comp.name || "",
      start_date: comp.start_date || "",
      end_date: comp.end_date || "",
      total_rounds: comp.total_rounds || 10,
      allow_selective_entry: comp.allow_selective_entry ?? true,
      recognize_score_of_2: comp.recognize_score_of_2 || false,
      hole_format: comp.hole_format || "18",
      course_id: comp.course_id || "",
      tee_id: comp.tee_id || ""
    });
    if (comp.course_id) fetchCourseDetails(comp.course_id);
    setShowEditComp(true);
  };

  const submitCreateComp = async (e) => {
    e.preventDefault();
    setCompFormSaving(true);
    try {
      const payload = { ...compForm };
      // Defensive clamp: if the user hits Enter while the rounds field is
      // mid-edit (empty / NaN), onBlur may not fire before submit. Coerce
      // here so the backend never sees an empty total_rounds.
      const tr = parseInt(payload.total_rounds, 10);
      payload.total_rounds = Number.isNaN(tr) || tr < 1 ? 1 : tr;
      if (!payload.course_id) { delete payload.course_id; delete payload.tee_id; }
      else if (!payload.tee_id) { delete payload.tee_id; }
      await axios.post(`${API}/admin/competitions`, payload, { withCredentials: true });
      toast.success("Competition created");
      setShowCreateComp(false);
      fetchAdminData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create competition");
    } finally { setCompFormSaving(false); }
  };

  const submitEditComp = async (e) => {
    e.preventDefault();
    if (!editingComp) return;
    setCompFormSaving(true);
    try {
      const payload = { ...compForm };
      // Same defensive clamp as create (see comment above).
      const tr = parseInt(payload.total_rounds, 10);
      payload.total_rounds = Number.isNaN(tr) || tr < 1 ? 1 : tr;
      payload.course_id = payload.course_id || null;
      payload.tee_id = payload.course_id ? (payload.tee_id || null) : null;
      await axios.put(`${API}/admin/competitions/${editingComp.id}`, payload, { withCredentials: true });
      toast.success("Competition updated");
      setShowEditComp(false);
      setEditingComp(null);
      fetchAdminData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update competition");
    } finally { setCompFormSaving(false); }
  };

  // Delete handler — fires after admin confirms in the destructive
  // AlertDialog. Hits the same /admin/competitions/{id} DELETE endpoint
  // the (now-buried) View-page Delete button uses, so behaviour is
  // identical: cascades through players / rounds / best_scores / messages
  // on the backend.
  const handleDeleteComp = async () => {
    if (!deletingComp) return;
    setDeleteSubmitting(true);
    try {
      await axios.delete(`${API}/admin/competitions/${deletingComp.id}`, { withCredentials: true });
      toast.success(`Deleted ${deletingComp.name}`);
      setDeletingComp(null);
      setShowEditComp(false);
      setEditingComp(null);
      fetchAdminData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete competition");
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const fetchUnreadGeneralMessages = async () => {
    try {
      const r = await axios.get(`${API}/messages/unread`, { withCredentials: true });
      setUnreadMessages(r.data || []);
      if ((r.data || []).length > 0) setShowMessagePopup(true);
    } catch (e) { console.error("Unread msgs failed", e); }
  };

  const handleMarkRead = async (id) => {
    try { await axios.put(`${API}/messages/${id}/mark-read`, {}, { withCredentials: true }); }
    catch (e) { console.error(e); }
  };

  const handleClosePopup = () => { setShowMessagePopup(false); setUnreadMessages([]); };
  const handleLogout = async () => { await logout(); navigate("/"); };

  if (!user) return null;

  // ==== Player view ====
  const isPlayer = user.role === "player";
  const isAdmin = user.role === "admin";
  // Sort joined competitions by closest end-date first (most urgent on top).
  // Filter out ended comps the same way the joinable list does — backend's
  // /api/player/my-competitions returns ALL the player's registrations
  // (including past ones) but the dashboard's "Your competitions" card
  // should only show live ones; past comps live in their own panel below.
  const todayIso = new Date().toISOString().slice(0, 10);
  const activeComps = (competitions || [])
    .filter((c) => c.status !== "ended" && (!c.end_date || c.end_date >= todayIso))
    .slice()
    .sort((a, b) => {
      const ea = a.end_date ? new Date(a.end_date).getTime() : Infinity;
      const eb = b.end_date ? new Date(b.end_date).getTime() : Infinity;
      return ea - eb;
    });
  // Past competitions for the history panel — most-recent first.
  const sortedPastComps = (pastComps || [])
    .slice()
    .sort((a, b) => {
      const ea = a.end_date ? new Date(a.end_date).getTime() : 0;
      const eb = b.end_date ? new Date(b.end_date).getTime() : 0;
      return eb - ea;
    });
  const hasComps = activeComps.length > 0;
  const sortedAvailable = (availableComps || [])
    .slice()
    .sort((a, b) => {
      const ea = a.end_date ? new Date(a.end_date).getTime() : Infinity;
      const eb = b.end_date ? new Date(b.end_date).getTime() : Infinity;
      return ea - eb;
    });
  const initials = (user.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const activeAdminComps = (adminCompetitions || []).filter((c) => c.status !== "ended");

  return (
    <div className="min-h-screen bg-app flex flex-col">
      <InstallAppBanner />
      {isAdmin && <AdminNavBar />}

      {/* Player-only top bar (admin uses AdminNavBar) */}
      {isPlayer && (
        <header className="border-b border-line bg-app/80 backdrop-blur sticky top-0 z-30">
          <div className="container mx-auto max-w-5xl px-5 sm:px-8 h-14 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <Logo variant="icon" size={28} />
              <span className="text-[15px] font-semibold tracking-tight text-ink">Golf Eclectic</span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setShowGuide(true)} className="ge-btn-ghost text-[12.5px] hidden sm:inline-flex" data-testid="guide-btn">
                <BookOpen className="w-3.5 h-3.5" /> Guide
              </button>
              <button onClick={handleLogout} className="ge-btn-ghost text-[12.5px]" data-testid="logout-button">
                <LogOut className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Sign out</span>
              </button>
              <div className="w-8 h-8 rounded-full bg-accent-tint text-accent-deep grid place-items-center text-[13px] font-semibold ml-1">
                {initials}
              </div>
            </div>
          </div>
        </header>
      )}

      <main className="flex-1 container mx-auto max-w-5xl px-5 sm:px-8 py-8 sm:py-10">

        {/* ===================== PLAYER ===================== */}
        {isPlayer && (
          <>
            <section className="mb-8">
              <p className="ge-eyebrow">Welcome back</p>
              <h1 className="text-3xl sm:text-4xl font-semibold tracking-[-0.02em] text-ink mt-1.5">{user.name}</h1>
              <p className="ge-page-sub mt-1.5">
                {hasComps
                  ? `${activeComps.length} active competition${activeComps.length === 1 ? "" : "s"}${sortedAvailable.length > 0 ? ` · ${sortedAvailable.length} more available to join` : ""} · keep building your perfect round.`
                  : sortedAvailable.length > 0
                    ? `${sortedAvailable.length} competition${sortedAvailable.length === 1 ? "" : "s"} open to join — pick one below.`
                    : "Once your club admin starts an eclectic, it'll appear here."}
              </p>
            </section>

            {/* Prominent CTA banner whenever there are comps available to join — hard to miss on mobile */}
            {sortedAvailable.length > 0 && (
              <section
                data-testid="new-comp-banner"
                className="mb-6 rounded-2xl border-2 border-accent/40 bg-accent-tint p-4 sm:p-5 flex items-center gap-3 sm:gap-4"
              >
                <div className="w-10 h-10 rounded-full bg-accent text-white grid place-items-center flex-shrink-0 shadow-sm">
                  <Plus className="w-5 h-5" strokeWidth={2.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] sm:text-[15px] font-semibold text-accent-deep tracking-tight">
                    {sortedAvailable.length === 1
                      ? "1 new competition is open to join"
                      : `${sortedAvailable.length} new competitions are open to join`}
                  </p>
                  <p className="text-[12.5px] text-accent-deep/80 mt-0.5 truncate">
                    {sortedAvailable.slice(0, 2).map((c) => c.name).join(" · ")}{sortedAvailable.length > 2 ? ` +${sortedAvailable.length - 2} more` : ""}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowAvailable(true);
                    setTimeout(() => {
                      document.querySelector('[data-testid="available-card"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
                    }, 50);
                  }}
                  className="ge-btn-primary text-[12.5px] !px-3 !py-2 flex-shrink-0"
                  data-testid="new-comp-banner-cta"
                >
                  View
                </button>
              </section>
            )}

            <Tabs defaultValue="overview" className="space-y-6">
              <TabsList className="ge-tabs">
                <TabsTrigger value="overview" className="ge-tab" data-testid="tab-overview">Overview</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-6">
                {/* Active competitions list */}
                <section data-testid="my-competitions-card" className="ge-section">
                  <div className="ge-section-head">
                    <div className="flex items-center gap-2">
                      <h2 className="ge-section-title">Your competitions</h2>
                      <span className="ge-pill">{activeComps.length}</span>
                    </div>
                    {sortedAvailable.length > 0 && (
                      <button
                        onClick={() => setShowAvailable((v) => !v)}
                        className="ge-btn-ghost text-[12.5px]"
                        data-testid="toggle-available-btn"
                      >
                        {showAvailable ? "Hide" : `${sortedAvailable.length} available to join`} <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showAvailable ? "rotate-90" : ""}`} />
                      </button>
                    )}
                  </div>

                  {hasComps ? (
                    <div className="divide-y divide-line">
                      {activeComps.map((c) => {
                        const justJoined = c.id === justJoinedId;
                        return (
                        <div
                          key={c.id}
                          data-testid={`comp-row-${c.id}`}
                          onClick={() => navigate(`/player/competitions/${c.id}/summary`)}
                          className={`px-5 py-4 flex items-center gap-4 cursor-pointer transition-colors ${justJoined ? "bg-accent-tint animate-pulse" : "hover:bg-app"}`}
                        >
                          <div className="w-10 h-10 rounded-lg bg-accent-tint grid place-items-center flex-shrink-0">
                            <img src="/logo.png" alt="" className="w-6 h-6" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-ink tracking-tight truncate">{c.name}</p>
                              {c.player_status === "withdrawn" && (
                                <span
                                  data-testid={`withdrawn-chip-${c.id}`}
                                  className="inline-flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-[0.06em] px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-200"
                                  title="You withdrew — rejoin within 7 days to restore all scores"
                                >
                                  Withdrawn
                                </span>
                              )}
                              {justJoined && (
                                <span
                                  data-testid={`just-joined-${c.id}`}
                                  className="inline-flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-[0.06em] px-1.5 py-0.5 rounded bg-accent text-white"
                                >
                                  Just joined
                                </span>
                              )}
                              {/* Rounds-completed pill — green check when player has finished every round */}
                              {(() => {
                                const played = c.rounds_played ?? 0;
                                const total = c.total_rounds ?? 0;
                                if (total === 0) return null;
                                if (played >= total) {
                                  return (
                                    <span
                                      data-testid={`rounds-complete-${c.id}`}
                                      className="inline-flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-[0.06em] px-1.5 py-0.5 rounded bg-accent-tint text-accent-deep border border-accent/30"
                                      title="You've played every round in this competition"
                                    >
                                      ✓ All rounds played
                                    </span>
                                  );
                                }
                                if (played > 0) {
                                  return (
                                    <span
                                      data-testid={`rounds-progress-${c.id}`}
                                      className="inline-flex items-center text-[10.5px] font-semibold uppercase tracking-[0.06em] px-1.5 py-0.5 rounded bg-app text-ink-muted border border-line tabular-nums"
                                    >
                                      {played}/{total} rounds
                                    </span>
                                  );
                                }
                                return null;
                              })()}
                              {(() => {
                                const u = computeUrgency(c.end_date);
                                if (!u) return null;
                                const cls = u.tone === "danger"
                                  ? "bg-red-tint text-red border border-red/30"
                                  : "bg-warm-tint text-warm border border-warm/30";
                                return (
                                  <span
                                    data-testid={`urgency-${c.id}`}
                                    className={`inline-flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-[0.06em] px-1.5 py-0.5 rounded ${cls}`}
                                  >
                                    {u.label}
                                  </span>
                                );
                              })()}
                            </div>
                            <p className="text-[12.5px] text-ink-muted mt-0.5 flex items-center gap-1.5">
                              <CalendarDays className="w-3.5 h-3.5" />
                              {formatUK(c.start_date)} → {formatUK(c.end_date)}
                              {c.hole_format && c.hole_format !== "18" && (
                                <span className="ml-1 ge-pill">{c.hole_format === "front_9" ? "Front 9" : "Back 9"}</span>
                              )}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {c.player_status === "withdrawn" ? (
                              // 7-day self-serve rejoin — replaces the previous
                              // "you're out, contact admin" dead-end. Shown only
                              // for comps that were soft-withdrawn within the
                              // last week (backend filters older ones).
                              <button
                                onClick={(e) => { e.stopPropagation(); handleRejoin(c); }}
                                className="ge-btn-accent text-[12.5px] !px-3 !py-1.5 inline-flex items-center gap-1.5"
                                data-testid={`rejoin-${c.id}-btn`}
                                title="Rejoin — your scores are still saved"
                              >
                                <RotateCcw className="w-3.5 h-3.5" />
                                Rejoin
                              </button>
                            ) : (
                              <>
                                {((c.rounds_played ?? 0) < (c.total_rounds ?? 0)) && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); navigate(`/player/competitions/${c.id}/enter-score`); }}
                                    className="ge-btn-accent text-[12.5px] !px-3 !py-1.5"
                                    data-testid={`enter-score-${c.id}-btn`}
                                  >
                                    Enter score
                                  </button>
                                )}
                                {/* Withdraw now lives inside a kebab menu (⋮) —
                                    two deliberate taps + typed-confirm inside
                                    the dialog. Fixes the mobile-fat-finger
                                    incident where a player accidentally
                                    withdrew and had to be re-added by admin. */}
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      onClick={(e) => e.stopPropagation()}
                                      className="text-ink-muted hover:text-ink transition-colors p-1.5 rounded hover:bg-ink-tint"
                                      data-testid={`comp-menu-${c.id}-btn`}
                                      title="More options"
                                      aria-label="More options"
                                    >
                                      <MoreVertical className="w-4 h-4" />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="bg-white">
                                    <DropdownMenuItem
                                      onClick={(e) => { e.stopPropagation(); setWithdrawingComp({ id: c.id, name: c.name }); }}
                                      className="text-red-600 focus:text-red-700 focus:bg-red-50 cursor-pointer"
                                      data-testid={`withdraw-${c.id}-btn`}
                                    >
                                      <LogOut className="w-3.5 h-3.5 mr-2" />
                                      Withdraw from competition
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </>
                            )}
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="px-5 py-12 text-center">
                      <Flag className="w-7 h-7 text-ink-soft mx-auto mb-3" />
                      <p className="text-ink font-medium text-[15px]">No active competitions</p>
                      <p className="text-[13.5px] text-ink-muted mt-1 mb-1">
                        {sortedAvailable.length > 0
                          ? `${sortedAvailable.length} competition${sortedAvailable.length === 1 ? "" : "s"} open to join below.`
                          : "Once your club admin starts an eclectic, you'll see it here."}
                      </p>
                    </div>
                  )}
                </section>

                {/* Available to join — collapsible */}
                {sortedAvailable.length > 0 && showAvailable && (
                  <section data-testid="available-card" className="ge-section">
                    <div className="ge-section-head">
                      <div className="flex items-center gap-2">
                        <h2 className="ge-section-title">Available to join</h2>
                        <span className="ge-pill">{sortedAvailable.length}</span>
                      </div>
                    </div>
                    <div className="divide-y divide-line">
                      {sortedAvailable.map((c) => (
                        <div
                          key={c.id}
                          data-testid={`available-row-${c.id}`}
                          className="px-5 py-4 flex items-center gap-4 hover:bg-app transition-colors"
                        >
                          <div className="w-10 h-10 rounded-lg bg-app text-ink-muted grid place-items-center flex-shrink-0 border border-line">
                            <Flag className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-ink tracking-tight truncate">{c.name}</p>
                            <p className="text-[12.5px] text-ink-muted mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                              <span className="inline-flex items-center gap-1.5">
                                <CalendarDays className="w-3.5 h-3.5" />
                                {formatUK(c.start_date)} → {formatUK(c.end_date)}
                              </span>
                              <span className="text-ink-soft">·</span>
                              <span>
                                {c.hole_format === "front_9" ? "Front 9" : c.hole_format === "back_9" ? "Back 9" : "18 holes"}
                                {c.total_rounds ? ` · ${c.total_rounds} round${c.total_rounds === 1 ? "" : "s"}` : ""}
                              </span>
                              <span className="text-ink-soft">·</span>
                              <span className="inline-flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {c.player_count || 0} player{c.player_count === 1 ? "" : "s"}
                              </span>
                            </p>
                          </div>
                          <button
                            onClick={() => handleJoin(c.id)}
                            disabled={joiningId === c.id}
                            className="ge-btn-primary text-[12.5px] !px-3.5 !py-1.5 disabled:opacity-40"
                            data-testid={`join-${c.id}-btn`}
                          >
                            {joiningId === c.id ? "Joining…" : "Join"}
                          </button>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {/* Past competitions — collapsible read-only history. Lets a
                    player drill back into their Best Eclectic from any comp
                    they participated in (active, ended, or archived). Sorted
                    most-recent-first. Hidden when there are none so we don't
                    add an empty section to a fresh player's dashboard. */}
                {sortedPastComps.length > 0 && (
                  <section data-testid="past-comps-card" className="ge-section">
                    <div className="ge-section-head">
                      <div className="flex items-center gap-2">
                        <h2 className="ge-section-title">Past competitions</h2>
                        <span className="ge-pill">{sortedPastComps.length}</span>
                      </div>
                      <p className="text-[12px] text-ink-muted">View your final results and best eclectic</p>
                    </div>
                    <div className="divide-y divide-line">
                      {sortedPastComps.map((c) => (
                        <div
                          key={c.id}
                          data-testid={`past-comp-row-${c.id}`}
                          onClick={() => navigate(`/player/competitions/${c.id}/summary`)}
                          className="px-5 py-4 flex items-center gap-4 cursor-pointer hover:bg-app transition-colors"
                        >
                          <div className="w-10 h-10 rounded-lg bg-app grid place-items-center flex-shrink-0 border border-line">
                            <img src="/logo.png" alt="" className="w-5 h-5 opacity-70" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-ink tracking-tight truncate">{c.name}</p>
                              <span className="inline-flex items-center text-[10.5px] font-semibold uppercase tracking-[0.06em] px-1.5 py-0.5 rounded bg-app text-ink-muted border border-line">
                                Ended
                              </span>
                            </div>
                            <p className="text-[12.5px] text-ink-muted mt-0.5">
                              {c.start_date} → {c.end_date}
                              {(c.rounds_played ?? 0) > 0 && (
                                <span className="ml-2 tabular-nums">· {c.rounds_played}/{c.total_rounds} rounds played</span>
                              )}
                            </p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-ink-soft" />
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {/* Quick actions row */}
                <section className="grid grid-cols-3 gap-3">
                  <button
                    onClick={() => setShowGuide(true)}
                    className="ge-card p-4 text-left hover:border-line-strong hover:shadow-sm transition-all"
                    data-testid="qa-guide"
                  >
                    <BookOpen className="w-4 h-4 text-accent mb-2.5" />
                    <p className="text-[13.5px] font-semibold tracking-tight text-ink">Player guide</p>
                    <p className="text-[12px] text-ink-muted mt-0.5">How eclectic works</p>
                  </button>
                  <button
                    onClick={() => navigate("/settings")}
                    className="ge-card p-4 text-left hover:border-line-strong hover:shadow-sm transition-all"
                    data-testid="qa-settings"
                  >
                    <SettingsIcon className="w-4 h-4 text-accent mb-2.5" />
                    <p className="text-[13.5px] font-semibold tracking-tight text-ink">Settings</p>
                    <p className="text-[12px] text-ink-muted mt-0.5">Password, alerts</p>
                  </button>
                  <button
                    onClick={handleLogout}
                    className="ge-card p-4 text-left hover:border-line-strong hover:shadow-sm transition-all"
                    data-testid="qa-signout"
                  >
                    <LogOut className="w-4 h-4 text-ink-muted mb-2.5" />
                    <p className="text-[13.5px] font-semibold tracking-tight text-ink">Sign out</p>
                    <p className="text-[12px] text-ink-muted mt-0.5">Switch accounts</p>
                  </button>
                </section>
              </TabsContent>
            </Tabs>
          </>
        )}

        {/* ===================== ADMIN ===================== */}
        {isAdmin && (
          <>
            <section className="mb-8 flex flex-wrap items-end justify-between gap-3">
              <div>
                <p className="ge-eyebrow">Admin</p>
                <h1 className="text-3xl sm:text-4xl font-semibold tracking-[-0.02em] text-ink mt-1.5">Hello {user.name?.split(" ")[0]}</h1>
                <p className="ge-page-sub mt-1.5">Run competitions, manage rosters, keep the data clean.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={openCreateComp} className="ge-btn-primary text-[12.5px]" data-testid="admin-new-comp-btn">
                  <Plus className="w-3.5 h-3.5" /> New competition
                </button>
              </div>
            </section>

            {/* Stat strip */}
            <section className="ge-stat-grid mb-8">
              <button
                onClick={() => { document.getElementById("admin-comps-section")?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                className="ge-stat text-left hover:bg-surface-2 transition-colors"
                data-testid="admin-stat-competitions"
              >
                <p className="ge-stat-label">Active competitions</p>
                <p className="ge-stat-value">{activeAdminComps.length}</p>
                <p className="ge-stat-delta">Of {adminCompetitions.length} total</p>
              </button>
              <button
                onClick={() => navigate("/admin/players")}
                className="ge-stat text-left hover:bg-surface-2 transition-colors"
                data-testid="admin-stat-players"
              >
                <p className="ge-stat-label">Players</p>
                <p className="ge-stat-value">{adminStats.players ?? "—"}</p>
                <p className="ge-stat-delta">Manage roster</p>
              </button>
              <button
                onClick={() => navigate("/admin/courses")}
                className="ge-stat text-left hover:bg-surface-2 transition-colors"
                data-testid="admin-stat-courses"
              >
                <p className="ge-stat-label">Courses</p>
                <p className="ge-stat-value">{adminStats.courses ?? "—"}</p>
                <p className="ge-stat-delta">Library + import</p>
              </button>
              <button
                onClick={() => navigate("/admin/utilities")}
                className="ge-stat text-left hover:bg-surface-2 transition-colors"
                data-testid="admin-stat-utilities"
              >
                <p className="ge-stat-label">Utilities</p>
                <p className="ge-stat-value">
                  <Database className="inline w-6 h-6 text-ink-muted" strokeWidth={1.6} />
                </p>
                <p className="ge-stat-delta">Health & cleanup</p>
              </button>
            </section>

            {/* All competitions (consolidated) */}
            <section id="admin-comps-section" className="ge-section mb-6">
              <div className="ge-section-head">
                <div className="flex items-center gap-2">
                  <h2 className="ge-section-title">All competitions</h2>
                  <span className="ge-pill">{adminCompetitions.length}</span>
                </div>
                <button onClick={openCreateComp} className="ge-btn-ghost text-[12.5px]" data-testid="admin-comps-create-inline">
                  <Plus className="w-3.5 h-3.5" /> Create
                </button>
              </div>
              {adminCompetitions.length > 0 ? (
                <div className="divide-y divide-line">
                  {adminCompetitions.map((c) => {
                    const ended = c.status === "ended";
                    return (
                      <div
                        key={c.id}
                        className="px-5 py-4 flex items-center gap-4 hover:bg-app transition-colors"
                        data-testid={`admin-comp-row-${c.id}`}
                      >
                        <div
                          onClick={() => navigate(`/admin/competitions/${c.id}`)}
                          className="flex items-center gap-4 flex-1 min-w-0 cursor-pointer"
                        >
                          <div className="w-9 h-9 rounded-lg bg-accent-tint grid place-items-center flex-shrink-0">
                            <img src="/logo.png" alt="" className="w-5 h-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-ink tracking-tight truncate">{c.name}</p>
                              {ended && (
                                <span className="text-[10.5px] tracking-wider px-1.5 py-0.5 rounded bg-app text-ink-muted border border-line uppercase font-semibold">Ended</span>
                              )}
                              {c.is_test_competition && (
                                <span className="text-[10.5px] tracking-wider px-1.5 py-0.5 rounded bg-warm-tint text-warm font-semibold uppercase">Test</span>
                              )}
                            </div>
                            <p className="text-[12.5px] text-ink-muted mt-0.5 flex items-center gap-1.5">
                              <CalendarDays className="w-3.5 h-3.5" />
                              {formatUK(c.start_date)} → {formatUK(c.end_date)}
                              {c.player_count != null && (
                                <>
                                  <span className="text-ink-soft">·</span>
                                  <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" />{c.player_count} player{c.player_count === 1 ? "" : "s"}</span>
                                </>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/admin/competitions/${c.id}`); }}
                            className="ge-btn-ghost text-[12px] !px-2 !py-1.5"
                            data-testid={`admin-comp-view-${c.id}`}
                            title="View competition"
                            aria-label="View competition"
                          >
                            <Eye className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                            <span className="hidden sm:inline ml-1">View</span>
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); openEditComp(c); }}
                            className="ge-btn-ghost text-[12px] !px-2 !py-1.5"
                            data-testid={`admin-comp-edit-${c.id}`}
                            title="Edit settings"
                            aria-label="Edit competition settings"
                          >
                            <Pencil className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                            <span className="hidden sm:inline ml-1">Edit</span>
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/admin/competitions/${c.id}/leaderboard`); }}
                            className="ge-btn-ghost text-[12px] !px-2 !py-1.5"
                            data-testid={`admin-comp-board-${c.id}`}
                            title="Open leaderboard"
                            aria-label="Open leaderboard"
                          >
                            <BarChart3 className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                            <span className="hidden sm:inline ml-1">Board</span>
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setAuditComp({ id: c.id, name: c.name, end_date: c.end_date }); }}
                            className="ge-btn-ghost text-[12px] !px-2 !py-1.5 relative"
                            data-testid={`admin-comp-audit-${c.id}`}
                            title={c.has_recent_audit_changes ? "Changes in the last 24h — view audit log" : "View audit log"}
                            aria-label="View audit log"
                          >
                            <Shield className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                            <span className="hidden sm:inline ml-1">Audit</span>
                            {c.has_recent_audit_changes && (
                              <span
                                data-testid={`admin-comp-audit-dot-${c.id}`}
                                aria-label="Recent changes"
                                className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-red-500 ring-2 ring-white"
                              />
                            )}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="px-5 py-10 text-center">
                  <ClipboardList className="w-7 h-7 text-ink-soft mx-auto mb-2" />
                  <p className="text-ink font-medium text-[15px]">No competitions yet</p>
                  <p className="text-[13px] text-ink-muted mt-1 mb-4">Create one to get players entering scores.</p>
                  <button onClick={openCreateComp} className="ge-btn-primary text-[13px]" data-testid="admin-empty-create-btn">
                    <Plus className="w-3.5 h-3.5" /> New competition
                  </button>
                </div>
              )}
            </section>

            {/* Secondary nav grid */}
            <section className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { i: Mail, t: "Email players", to: "/admin/send-email", testid: "qa-email" },
                { i: Users, t: "Players", to: "/admin/players", testid: "qa-players" },
                { i: Flag, t: "Courses", to: "/admin/courses", testid: "qa-courses" },
                { i: BookOpen, t: "Admin guide", onClick: () => setShowGuide(true), testid: "qa-admin-guide" },
              ].map((tile) => (
                <button
                  key={tile.t}
                  onClick={tile.onClick || (() => navigate(tile.to))}
                  className="ge-card p-4 text-left hover:border-line-strong hover:shadow-sm transition-all"
                  data-testid={tile.testid}
                >
                  <tile.i className="w-4 h-4 text-accent mb-2.5" />
                  <p className="text-[13.5px] font-semibold tracking-tight text-ink">{tile.t}</p>
                </button>
              ))}
            </section>
          </>
        )}
      </main>

      {/* Broadcast popup msgs (player) */}
      {isPlayer && showMessagePopup && unreadMessages.length > 0 && (
        <MessagePopup messages={unreadMessages} onClose={handleClosePopup} onMarkRead={handleMarkRead} />
      )}

      {/* Player Guide */}
      {isPlayer && (
        <Dialog open={showGuide} onOpenChange={setShowGuide}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="text-2xl">Player's guide</DialogTitle></DialogHeader>
            <PlayerGuide />
          </DialogContent>
        </Dialog>
      )}

      {/* Admin Guide */}
      {isAdmin && (
        <Dialog open={showGuide} onOpenChange={setShowGuide}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="text-2xl">Administrator's guide</DialogTitle></DialogHeader>
            <AdminGuide />
          </DialogContent>
        </Dialog>
      )}

      {/* Create / Edit competition dialog (admin) */}
      {isAdmin && (
        <CompetitionFormDialog
          mode="create"
          open={showCreateComp}
          onOpenChange={setShowCreateComp}
          form={compForm}
          setForm={setCompForm}
          courseOptions={courseOptions}
          courseDetails={courseDetails}
          fetchCourseDetails={fetchCourseDetails}
          saving={compFormSaving}
          onSubmit={submitCreateComp}
        />
      )}
      {isAdmin && (
        <CompetitionFormDialog
          mode="edit"
          open={showEditComp}
          onOpenChange={(v) => { setShowEditComp(v); if (!v) setEditingComp(null); }}
          form={compForm}
          setForm={setCompForm}
          courseOptions={courseOptions}
          courseDetails={courseDetails}
          fetchCourseDetails={fetchCourseDetails}
          saving={compFormSaving}
          onSubmit={submitEditComp}
          onRequestDelete={() => editingComp && setDeletingComp({ id: editingComp.id, name: editingComp.name })}
        />
      )}

      {/* Delete-comp confirmation — destructive AlertDialog driven by
          deletingComp state. Opened from the "Delete competition" button
          at the bottom of the Edit modal so admins have a discoverable
          path to remove a comp without hunting through the View page. */}
      <DestructiveConfirmDialog
        open={!!deletingComp}
        onOpenChange={(v) => { if (!v) setDeletingComp(null); }}
        title={`Delete ${deletingComp?.name || "this competition"}?`}
        description="This will permanently delete the competition, every player's rounds and scores, the leaderboard and all related messages. This action cannot be undone."
        confirmLabel="Delete competition"
        confirmingLabel="Deleting…"
        submitting={deleteSubmitting}
        onConfirm={handleDeleteComp}
        dialogTestId="delete-comp-confirm-dialog"
        cancelTestId="delete-comp-cancel-btn"
        confirmTestId="delete-comp-confirm-btn"
      />

      {/* Withdraw confirmation — destructive AlertDialog driven by
          withdrawingComp state. Opened from the per-row Withdraw button in
          "Your competitions"; confirming fires handleWithdraw which calls
          POST /competitions/{id}/withdraw and refreshes the comp list. */}
      <DestructiveConfirmDialog
        open={!!withdrawingComp}
        onOpenChange={(v) => { if (!v) setWithdrawingComp(null); }}
        title={`Leave ${withdrawingComp?.name || "this competition"}?`}
        description="You'll be removed from the leaderboard immediately. Your scores are preserved for 7 days — you can Rejoin from your dashboard within that window. After 7 days your admin will have to add you back manually."
        confirmLabel="Withdraw"
        confirmingLabel="Withdrawing…"
        submitting={withdrawSubmitting}
        onConfirm={handleWithdraw}
        typedConfirmation="WITHDRAW"
        dialogTestId="withdraw-confirm-dialog"
        cancelTestId="withdraw-cancel-btn"
        confirmTestId="withdraw-confirm-btn"
      />

      <AppFooter />

      {/* Audit Log Modal — opened from the per-row Audit button in the admin section */}
      <AuditLogModal
        open={!!auditComp}
        onClose={() => setAuditComp(null)}
        competitionId={auditComp?.id}
        competitionName={auditComp?.name}
        competitionEndDate={auditComp?.end_date}
      />
    </div>
  );
}
